import { memo, useEffect, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignUpPage from './pages/SignUpPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPasswordPage from './pages/ResetPasswordPage';
import GoogleCallbackPage from './pages/GoogleCallbackPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import SignupVerifyPage from './pages/SignupVerifyPage';
import VerifyEmailNoticePage from './pages/VerifyEmailNoticePage';
import AdminDashboard from './pages/AdminDashboard';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import InstitutionDashboard from './pages/InstitutionDashboard';
import TeacherDashboard from './pages/TeacherDashboard';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscribePage from './pages/SubscribePage';
import TrialExpiredPage from './pages/TrialExpiredPage';
import DemoExpiredPage from './pages/DemoExpiredPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import RenewSubscriptionPage from './pages/RenewSubscriptionPage';
import LevelTestPage from './pages/LevelTestPage';
import LevelTestResultsPage from './pages/LevelTestResultsPage';
import DemoConfirmationPage from './pages/DemoConfirmationPage';
import ContactPage from './pages/ContactPage/ContactPage';
import LevelsPage from './pages/CoursesPage';
import LevelDetailsPage from './pages/LevelDetailsPage';
import LessonsPage from './pages/LessonsPage';
import ErrorPage from './pages/ErrorPage';
import RedeemPage from './pages/RedeemPage';
import UIConfigDashboard from './components/UIConfigDashboard';
import BecomePartnerPage from './pages/BecomePartnerPage';
import AdminAutomation from './pages/AdminAutomation';

import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import ErrorBoundary from './components/ErrorBoundary';
import { useUIConfig } from './hooks/useUIConfig';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import { MetaPixelProvider } from './contexts/MetaPixelContext';
import { ConsentProvider } from './contexts/ConsentContext';
import AnalyticsIdentify from './components/AnalyticsIdentify';
import CookieBanner from './components/CookieBanner';
import ExitIntentPopup from './components/ExitIntentPopup';
import './App.css';


const App = memo(() => {
  const { applyCSS } = useUIConfig();
  const { i18n } = useTranslation();
  const [consentRequired, setConsentRequired] = useState(false);

  // Update body class and document direction based on language
  useEffect(() => {
    if (i18n.language === 'ar') {
      document.body.classList.add('lang-ar');
      document.body.classList.remove('lang-en');
      // Set RTL direction for proper Arabic/mixed text rendering
      document.documentElement.dir = 'rtl';
      document.body.style.direction = 'rtl';
    } else {
      document.body.classList.add('lang-en');
      document.body.classList.remove('lang-ar');
      // Set LTR direction for English
      document.documentElement.dir = 'ltr';
      document.body.style.direction = 'ltr';
    }
  }, [i18n.language]);

  // Apply CSS when app starts
  useEffect(() => {
    applyCSS();
  }, [applyCSS]);

  // Load consent setting from backend (public, no auth)
  useEffect(() => {
    const loadConsentSetting = async () => {
      try {
        let baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

        // Use '/api' in production - Coolify's Traefik strips the first /api prefix
        if (process.env.NODE_ENV === 'production') {
          baseUrl = '/api';
        }

        const response = await fetch(`${baseUrl}/api/v1/config/public`);
        if (!response.ok) {
          return;
        }
        const data = await response.json();
        if (typeof data.consent_required === 'boolean') {
          setConsentRequired(data.consent_required);
        }
      } catch (error) {
        console.error('Failed to load consent setting:', error);
      }
    };

    loadConsentSetting();
  }, []);

  return (
    <div className="App">
      <ScrollToTop />
      <ConsentProvider consentRequired={consentRequired}>
        <MetaPixelProvider>
          <AnalyticsProvider>
            <AnalyticsIdentify />
            <CookieBanner />
            <ExitIntentPopup />
            <Routes>
              {/* Localized landing pages for SEO */}
              <Route path="/en" element={<HomePage lang="en" />} />
              <Route path="/ar" element={<HomePage lang="ar" />} />

              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/signup-verify" element={<SignupVerifyPage />} />
              <Route path="/verify-email-notice" element={<VerifyEmailNoticePage />} />
              <Route path="/demo-confirmation" element={<DemoConfirmationPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/become-partner" element={<BecomePartnerPage />} />
              <Route path="/subscribe" element={<SubscribePage />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/institution"
                element={
                  <ProtectedRoute allowedRoles={['institution_admin']}>
                    <InstitutionDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/teacher"
                element={
                  <ProtectedRoute allowedRoles={['teacher']}>
                    <TeacherDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={['super_admin']}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/automation"
                element={
                  <ProtectedRoute allowedRoles={['super_admin']}>
                    <AdminAutomation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute>
                    <SubscriptionPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/trial-expired"
                element={
                  <ProtectedRoute>
                    <TrialExpiredPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/demo-expired"
                element={
                  <ProtectedRoute>
                    <DemoExpiredPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment-success"
                element={
                  <ProtectedRoute>
                    <PaymentSuccessPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/renew"
                element={
                  <ProtectedRoute>
                    <RenewSubscriptionPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/level-test"
                element={<LevelTestPage />}
              />
              <Route
                path="/level-test/results"
                element={<LevelTestResultsPage />}
              />
              <Route
                path="/levels"
                element={
                  <ProtectedRoute>
                    <LevelsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/levels/:levelParam"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <LevelDetailsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/levels/:levelParam/lessons"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <LessonsPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/redeem"
                element={
                  <ProtectedRoute>
                    <RedeemPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/ui-config" element={<UIConfigDashboard />} />

              <Route path="*" element={<ErrorPage />} />
            </Routes>
          </AnalyticsProvider>
        </MetaPixelProvider>
      </ConsentProvider>
    </div>
  );
});

export default App;
